import type { ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Switch } from '@/app/components/ui/switch';
import { Separator } from '@/app/components/ui/separator';
import { TabsContent } from '@/app/components/ui/tabs';
import { CharBar } from '../CharBar';
import type { EditorReady } from './types';

type Props = {
  ed: EditorReady;
  adBar: ReactNode;
  fieldPalette: ReactNode;
};

export function UrlsTab({ ed, adBar, fieldPalette }: Props) {
  const {
    templateSet, sampleDataRow, isMultiAd, currentAd, activeAdIndex,
    updateTemplateContent, updateAd,
    getPreviewText, getCharInfo, inputProps,
  } = ed;

  return (
    <TabsContent value="urls" className="space-y-4">
      {fieldPalette}
      <Card>
        <CardHeader>
          <CardTitle>URL Configuration</CardTitle>
          <CardDescription>Configure final URLs and display paths</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isMultiAd && (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Use different URLs per ad</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">When enabled, each ad can have its own URL and display paths</p>
                </div>
                <Switch
                  checked={templateSet.template_content.use_per_ad_urls ?? false}
                  onCheckedChange={checked => updateTemplateContent('use_per_ad_urls', checked)}
                />
              </div>
              <Separator />
            </>
          )}
          {templateSet.template_content.use_per_ad_urls && isMultiAd ? (
            <>
              {adBar}
              {(() => {
                const ad = currentAd;
                if (!ad) return null;
                return (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Final URL Template <span className="text-muted-foreground/60 text-xs ml-1">Ad {activeAdIndex}</span></Label>
                      <Input
                        value={ad.final_url_template ?? templateSet.template_content.final_url_template}
                        onChange={e => updateAd(activeAdIndex, { final_url_template: e.target.value })}
                        placeholder="e.g., https://example.com/{nom_slug}"
                      />
                      {(ad.final_url_template ?? templateSet.template_content.final_url_template) && sampleDataRow && (
                        <div className="bg-muted/50 p-2 rounded text-sm border-l-2 border-primary">
                          <span className="text-muted-foreground text-xs">Preview: </span>
                          <span className="font-mono text-blue-600 dark:text-blue-400 text-xs">{getPreviewText(ad.final_url_template ?? templateSet.template_content.final_url_template)}</span>
                        </div>
                      )}
                    </div>
                    <Separator />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Path 1 <span className="text-muted-foreground/60 font-mono text-xs ml-1">15 chars</span></Label>
                        <Input
                          value={ad.path1_template ?? templateSet.template_content.path1_template}
                          onChange={e => updateAd(activeAdIndex, { path1_template: e.target.value })}
                          placeholder="e.g., {region_nom}"
                        />
                        <CharBar {...getCharInfo(ad.path1_template ?? templateSet.template_content.path1_template, 15)} limit={15} />
                      </div>
                      <div className="space-y-2">
                        <Label>Path 2 <span className="text-muted-foreground/60 font-mono text-xs ml-1">15 chars</span></Label>
                        <Input
                          value={ad.path2_template ?? templateSet.template_content.path2_template}
                          onChange={e => updateAd(activeAdIndex, { path2_template: e.target.value })}
                          placeholder="e.g., {nom_standard}"
                        />
                        <CharBar {...getCharInfo(ad.path2_template ?? templateSet.template_content.path2_template, 15)} limit={15} />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Final URL Template</Label>
                <Input
                  value={templateSet.template_content.final_url_template}
                  onChange={e => updateTemplateContent('final_url_template', e.target.value)}
                  placeholder="e.g., https://example.com/{nom_slug}"
                  {...inputProps('final_url_template')}
                />
                {templateSet.template_content.final_url_template && sampleDataRow && (
                  <div className="bg-muted/50 p-2 rounded text-sm border-l-2 border-primary">
                    <span className="text-muted-foreground text-xs">Preview: </span>
                    <span className="font-mono text-blue-600 dark:text-blue-400 text-xs">{getPreviewText(templateSet.template_content.final_url_template)}</span>
                  </div>
                )}
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Path 1 <span className="text-muted-foreground/60 font-mono text-xs ml-1">15 chars</span></Label>
                  <Input
                    value={templateSet.template_content.path1_template}
                    onChange={e => updateTemplateContent('path1_template', e.target.value)}
                    placeholder="e.g., {region_nom}"
                    {...inputProps('path1_template')}
                  />
                  <CharBar {...getCharInfo(templateSet.template_content.path1_template, 15)} limit={15} />
                </div>
                <div className="space-y-2">
                  <Label>Path 2 <span className="text-muted-foreground/60 font-mono text-xs ml-1">15 chars</span></Label>
                  <Input
                    value={templateSet.template_content.path2_template}
                    onChange={e => updateTemplateContent('path2_template', e.target.value)}
                    placeholder="e.g., {nom_standard}"
                    {...inputProps('path2_template')}
                  />
                  <CharBar {...getCharInfo(templateSet.template_content.path2_template, 15)} limit={15} />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
